import { Clock } from 'lucide-react';
import { useCallback, useId, useRef, useState } from 'react';
import { cn } from './cn.js';
import { FloatingPanel } from './floating-panel.js';
import { DAY_MINUTES, formatMinutes, parseTimeText, toMinutes } from './time-text.js';

/// Um horário candidato do painel. Sem `unavailableReason` ele está livre; com, o painel mostra o
/// motivo ("ocupado", "bloqueado") em vez de esconder a opção. `offHours` marca o horário livre
/// que fica fora do expediente: continua escolhível, só aparece em outra cor.
export interface TimeSlot {
  time: string;
  unavailableReason?: string | undefined;
  offHours?: boolean | undefined;
}

export interface TimePickerProps {
  label: string;
  /// Horário civil `HH:MM`, ou `null` quando ainda não foi informado.
  value: string | null;
  onValueChange: (value: string | null) => void;
  /// Horários que o painel oferece. Sem eles, o painel oferece o dia inteiro, todos livres.
  slots?: TimeSlot[] | undefined;
  /// Passo das setas e da coluna de minutos.
  step?: number | undefined;
  placeholder?: string | undefined;
  error?: string | undefined;
  hint?: string | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
}

/// Seletor de horário: digitação livre para quem sabe a hora e painel de horas e minutos para quem
/// quer ver o que está livre. Substitui a lista de 96 horários, que obrigava a rolar atrás da hora
/// e não dizia por que um horário não servia.
export function TimePicker({
  label,
  value,
  onValueChange,
  slots,
  step = 15,
  placeholder = 'hh:mm',
  error,
  hint,
  disabled,
  className,
}: TimePickerProps) {
  const id = useId();
  const fieldRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value ?? '');
  const [syncedValue, setSyncedValue] = useState(value);
  const [typingError, setTypingError] = useState<string | null>(null);
  const close = useCallback(() => setOpen(false), []);

  // Quando o valor muda por fora — clique na grade, painel —, o texto acompanha. O ajuste acontece
  // no render para não pintar uma vez com o texto velho.
  if (value !== syncedValue) {
    setSyncedValue(value);
    setText(value ?? '');
    setTypingError(null);
  }

  const shownError = typingError ?? error;
  const descriptionId = shownError ? `${id}-error` : hint ? `${id}-hint` : undefined;

  const candidates: TimeSlot[] =
    slots ??
    Array.from({ length: DAY_MINUTES / step }, (_item, index) => ({
      time: formatMinutes(index * step),
    }));
  const reasons = new Map(candidates.map((slot) => [slot.time, slot.unavailableReason]));
  const free = new Set(
    candidates.filter((slot) => !slot.unavailableReason).map((slot) => slot.time),
  );
  const offHours = new Set(candidates.filter((slot) => slot.offHours).map((slot) => slot.time));
  const hasOffHours = offHours.size > 0;
  const inHours = [...free].filter((time) => !offHours.has(time)).length;
  const hours = [...new Set(candidates.map((slot) => Math.floor(toMinutes(slot.time) / 60)))];
  const selected = value === null ? null : toMinutes(value);
  const selectedHour = selected === null ? null : Math.floor(selected / 60);
  const selectedMinute = selected === null ? null : selected % 60;
  const minutes = Array.from({ length: 60 / step }, (_item, index) => index * step);
  if (selectedMinute !== null && !minutes.includes(selectedMinute)) {
    minutes.push(selectedMinute);
    minutes.sort((left, right) => left - right);
  }

  function choose(next: string | null) {
    setTypingError(null);
    setText(next ?? '');
    onValueChange(next);
  }

  function commit() {
    const parsed = parseTimeText(text);
    if (parsed === undefined) {
      setTypingError('Horário inválido. Use, por exemplo, 14:30.');
      return;
    }
    if (parsed === value) setText(parsed ?? '');
    else choose(parsed);
  }

  function stepBy(direction: 1 | -1) {
    const typed = parseTimeText(text);
    if (!typed) {
      const first =
        candidates.find((slot) => !slot.unavailableReason && !slot.offHours) ??
        candidates.find((slot) => !slot.unavailableReason) ??
        candidates[0];
      if (first) choose(first.time);
      return;
    }
    const current = toMinutes(typed);
    const next =
      direction === 1
        ? Math.floor(current / step) * step + step
        : Math.ceil(current / step) * step - step;
    choose(formatMinutes(Math.min(Math.max(next, 0), DAY_MINUTES - step)));
  }

  function hourHasFree(hour: number, withinHours = false) {
    return minutes.some((minute) => {
      const time = formatMinutes(hour * 60 + minute);
      return free.has(time) && !(withinHours && offHours.has(time));
    });
  }

  return (
    <div className={cn('min-w-0 space-y-2', className)}>
      <label htmlFor={id} className="block text-xs font-semibold text-content/70">
        {label}
      </label>
      <div className="relative min-w-0" ref={fieldRef}>
        <input
          id={id}
          value={text}
          disabled={disabled}
          inputMode="numeric"
          autoComplete="off"
          placeholder={placeholder}
          aria-invalid={Boolean(shownError)}
          aria-describedby={descriptionId}
          aria-expanded={open}
          aria-controls={`${id}-panel`}
          onChange={(event) => {
            setText(event.target.value);
            setTypingError(null);
          }}
          onClick={() => setOpen(true)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
              setOpen(false);
            } else if (event.key === 'Escape') {
              if (open) event.stopPropagation();
              setOpen(false);
            } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
              event.preventDefault();
              stepBy(event.key === 'ArrowUp' ? 1 : -1);
            }
          }}
          className={cn(
            'hue-input h-12 w-full min-w-0 rounded-xl border bg-surface-elevated pl-4 pr-12 text-sm tabular-nums text-content outline-none transition placeholder:text-content-faint focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-50',
            shownError ? 'border-danger/70' : 'border-surface-border',
          )}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((current) => !current)}
          aria-label={`Ver horários de ${label.toLocaleLowerCase('pt-BR')}`}
          aria-expanded={open}
          aria-controls={`${id}-panel`}
          className="absolute right-1 top-1/2 grid size-10 -translate-y-1/2 cursor-pointer place-items-center rounded-lg text-content-muted transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Clock className="size-4" />
        </button>

        {open ? (
          <FloatingPanel
            anchorRef={fieldRef}
            onDismiss={close}
            id={`${id}-panel`}
            role="dialog"
            aria-label={`Horários de ${label.toLocaleLowerCase('pt-BR')}`}
            className="w-[18rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card"
          >
            <div className="mb-2 flex items-center justify-between gap-2 px-1 text-xs text-content-muted">
              <span>
                {candidates.length === 0
                  ? 'Sem expediente neste dia'
                  : hasOffHours
                    ? `${inHours} ${inHours === 1 ? 'livre' : 'livres'} no expediente`
                    : `${free.size} ${free.size === 1 ? 'horário livre' : 'horários livres'}`}
              </span>
              <strong className="text-sm tabular-nums text-content">{value ?? '--:--'}</strong>
            </div>
            {candidates.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                <div
                  role="listbox"
                  aria-label="Hora"
                  className="max-h-56 space-y-0.5 overflow-y-auto rounded-xl border border-surface-border p-1"
                >
                  {hours.map((hour) => {
                    const isSelected = hour === selectedHour;
                    const available = hourHasFree(hour);
                    const overtimeOnly = available && hasOffHours && !hourHasFree(hour, true);
                    return (
                      <button
                        key={hour}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        ref={(node) => {
                          if (node && isSelected) node.scrollIntoView?.({ block: 'center' });
                        }}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          const keep =
                            selectedMinute ??
                            minutes.find((minute) => free.has(formatMinutes(hour * 60 + minute))) ??
                            0;
                          choose(formatMinutes(hour * 60 + keep));
                        }}
                        className={cn(
                          'flex min-h-9 w-full cursor-pointer items-center rounded-lg px-3 text-sm tabular-nums transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50',
                          isSelected
                            ? 'bg-brand-500 font-semibold text-on-brand'
                            : overtimeOnly
                              ? 'text-warning hover:bg-surface-elevated'
                              : available
                                ? 'font-semibold hover:bg-surface-elevated'
                                : 'text-content-faint hover:bg-surface-elevated',
                        )}
                      >
                        {String(hour).padStart(2, '0')}
                      </button>
                    );
                  })}
                </div>
                <div
                  role="listbox"
                  aria-label="Minuto"
                  className="max-h-56 space-y-0.5 overflow-y-auto rounded-xl border border-surface-border p-1"
                >
                  {minutes.map((minute) => {
                    const time =
                      selectedHour === null ? null : formatMinutes(selectedHour * 60 + minute);
                    const reason = time === null ? undefined : reasons.get(time);
                    const overtime = time !== null && !reason && offHours.has(time);
                    const isSelected = minute === selectedMinute;
                    return (
                      <button
                        key={minute}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        disabled={time === null}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          if (time === null) return;
                          choose(time);
                          setOpen(false);
                        }}
                        className={cn(
                          'flex min-h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-lg px-3 text-sm tabular-nums transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:cursor-not-allowed disabled:opacity-50',
                          isSelected
                            ? 'bg-brand-500 font-semibold text-on-brand'
                            : reason
                              ? 'text-content-faint hover:bg-surface-elevated'
                              : overtime
                                ? 'text-warning hover:bg-surface-elevated'
                                : 'font-semibold hover:bg-surface-elevated',
                        )}
                      >
                        <span>{String(minute).padStart(2, '0')}</span>
                        {reason ? (
                          <small className="truncate text-[10px]">{reason}</small>
                        ) : overtime ? (
                          <small className="truncate text-[10px]">fora do expediente</small>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
            <p className="mt-2 px-1 text-[11px] leading-4 text-content-muted">
              {hasOffHours
                ? 'Em amarelo, fora do expediente: dá para escolher. Em cinza, sem horário livre. Você também pode digitar.'
                : 'Horas em cinza não têm horário livre. Você também pode digitar.'}
            </p>
          </FloatingPanel>
        ) : null}
      </div>
      {shownError ? (
        <p id={descriptionId} role="alert" className="text-xs text-danger">
          {shownError}
        </p>
      ) : hint ? (
        <p id={descriptionId} className="text-xs leading-5 text-content-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
